/**
 * What the Network board renders today.
 *
 * Cato needs two things and NOVA currently has neither wired into the app: the
 * API key (which exists in Key Vault but is not injected into the backend's
 * environment) and the numeric account ID (which is mandatory on every Cato
 * query and cannot be discovered from a single-account API key — it has to be
 * read out of the Cato Management Application URL).
 *
 * So this is not a shrug. It names each missing piece, says exactly where to
 * get it, and shows what the board will render once it is there — while being
 * explicit that nothing on this page is simulated.
 */

import {
  ArrowDownUp,
  CircleCheck,
  Gauge,
  Hash,
  Icon,
  KeyRound,
  type LucideIcon,
  Radio,
  Router,
  SatelliteDish,
  ShieldCheck,
  TriangleAlert,
  Waypoints,
} from "@norican/icons";
import { Alert, Badge, Card, EmptyState, Separator, Typography } from "@norican/react";
import type { ReactNode } from "react";

import type { NetworkOverview } from "@/lib/api";

/** The Cato Management Application URL the account ID is read from. */
const CMA_URL = "https://cc.catonetworks.com";

interface RequirementProps {
  /** The environment variable the backend reads. */
  variable: string;
  /** What the value is. */
  title: string;
  /** Whether the backend can currently see it. */
  present: boolean;
  /** Icon for the requirement row. */
  icon: LucideIcon;
  /** How to obtain it — the part that makes this page actionable. */
  children: ReactNode;
}

function Requirement({ variable, title, present, icon, children }: RequirementProps) {
  return (
    <div className="flex gap-4">
      <div className="pt-0.5">
        <Icon
          icon={present ? CircleCheck : icon}
          size="md"
          tone={present ? "success" : "warning"}
        />
      </div>
      <div className="flex flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <Typography variant="label" as="span">
            {title}
          </Typography>
          <Typography
            variant="code"
            as="code"
            tone="muted"
            className="bg-bg-muted rounded-sm px-1.5 py-0.5 text-xs"
          >
            {variable}
          </Typography>
          <Badge tone={present ? "success" : "warning"} variant="subtle" size="sm">
            {present ? "Detected" : "Missing"}
          </Badge>
        </div>
        <Typography variant="body-sm" tone="muted">
          {children}
        </Typography>
      </div>
    </div>
  );
}

/** One line in the "what this board will show" preview. */
const PLANNED_PANELS: readonly { icon: LucideIcon; label: string; detail: string }[] = [
  { icon: Waypoints, label: "SASE health", detail: "Sites connected, degraded, disconnected" },
  { icon: Router, label: "Site status", detail: "PoP, uptime, hosts, last connected" },
  { icon: Radio, label: "Last-mile links", detail: "Per WAN port: ISP, tunnel uptime, reason" },
  { icon: ShieldCheck, label: "Socket fleet", detail: "Model, HA role, firmware skew, serial" },
  { icon: ArrowDownUp, label: "Bandwidth", detail: "Throughput against provisioned capacity" },
  { icon: Gauge, label: "Link quality", detail: "Loss, latency and jitter per site" },
  { icon: SatelliteDish, label: "Event feed", detail: "Site up/down and tunnel re-establish" },
];

interface NotConfiguredProps {
  /** The API's own account of what it can and cannot see. */
  overview: NetworkOverview;
}

export function NotConfigured({ overview }: NotConfiguredProps) {
  const keyPresent = overview.api_key_present;
  const accountPresent = overview.account_id_present;

  return (
    <div className="flex flex-col gap-4">
      <Card variant="outline" accent="left" accentTone="warning">
        <Card.Body>
          <EmptyState
            icon={<Icon icon={Waypoints} size="lg" tone="warning" />}
            title="Cato SASE is not connected yet"
            description="NOVA can read the WAN estate as soon as the two values below reach the backend. Until then this board shows nothing rather than something invented."
          />
        </Card.Body>

        <Separator />

        <Card.Body>
          <div className="flex flex-col gap-5">
            <Requirement
              variable="CATO_API_KEY"
              title="Cato API key"
              present={keyPresent}
              icon={KeyRound}
            >
              A Viewer (read-only) key is all NOVA needs — it never writes to Cato. The key already
              exists in Key Vault{" "}
              <Typography variant="code" as="span">
                nova-kv-dev-6636
              </Typography>{" "}
              as{" "}
              <Typography variant="code" as="span">
                cato-api-key
              </Typography>
              , but it is not yet injected into the backend&rsquo;s environment. It is also
              Terraform drift: the secret is not declared in{" "}
              <Typography variant="code" as="span">
                infra/modules/keyvault
              </Typography>
              , so it exists in Azure and nowhere in code.
            </Requirement>

            <Requirement
              variable="CATO_ACCOUNT_ID"
              title="Numeric Cato account ID"
              present={accountPresent}
              icon={Hash}
            >
              Cato requires this on essentially every query, and it cannot be discovered from a
              single-account API key — there is no &ldquo;which account am I?&rdquo; call. Read it
              out of the Cato Management Application URL: sign in at{" "}
              <a
                href={CMA_URL}
                target="_blank"
                rel="noreferrer noopener"
                className="text-accent underline underline-offset-2"
              >
                cc.catonetworks.com
              </a>{" "}
              and take the path segment straight after the{" "}
              <Typography variant="code" as="span">
                #/
              </Typography>{" "}
              —{" "}
              <Typography variant="code" as="span">
                https://cc.catonetworks.com/#/<strong>&lt;accountID&gt;</strong>/topology
              </Typography>
              .
            </Requirement>
          </div>
        </Card.Body>

        {overview.status_detail && (
          <Card.Footer>
            <Alert
              tone={keyPresent || accountPresent ? "warning" : "info"}
              icon={<Icon icon={TriangleAlert} size="sm" />}
              title="What the backend reports"
            >
              {overview.status_detail}
            </Alert>
          </Card.Footer>
        )}
      </Card>

      <Card variant="subtle">
        <Card.Body>
          <Typography variant="label" as="p" className="mb-3">
            What appears once it is connected
          </Typography>
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 xl:grid-cols-3">
            {PLANNED_PANELS.map((panel) => (
              <div key={panel.label} className="flex items-start gap-3">
                <Icon icon={panel.icon} size="sm" className="mt-0.5" />
                <div className="flex flex-col">
                  <Typography variant="body-sm">{panel.label}</Typography>
                  <Typography variant="caption" tone="muted">
                    {panel.detail}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
